import {
    Table,
    TableHead,
    TableCell,
    TableRow,
    TableBody,
    TableContainer,
    TableFooter,
    Grid,
    withTheme,
    Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import { useSwaggerClient } from '../../hooks/useSwaggerClient';
import Calendar from '../calendar';

const CartPageCalendar = ({ label, guestId, cart, guest }) => {
    const [overlap, setOverlap] = useState(false);
    const [startDate, setStartDate] = useState(moment().startOf('day'));
    const { showModal } = useModals();
    const [endDate, setEndDate] = useState(
        moment()
            .endOf('day')
            .add(6, 'days')
    );
    const [bookingsData, bookingsLoading] = useSwaggerClient(
        'users',
        'getBookingsByUserId',
        {
            userId: guestId,
            calendar: true,
            startDate: moment(startDate).format('YYYY-MM-DD'),
            endDate: moment(endDate).format('YYYY-MM-DD')
        },
        [],
        res => {
            if (cart) {
                return {
                    ...res,
                    bookingItems: res.bookingItems.concat(
                        cart.map(item => {
                            return {
                                id: item.activity_id,
                                activity: {
                                    name: item.activity.name
                                },
                                activity_start: item.start_time,
                                activity_end: item.end_time,
                                status: 'in_cart',
                                hash: '',
                                tickets: item.tickets
                            };
                        })
                    )
                };
            }

            return {
                ...res,
                bookingItems: res.bookingItems.map(e => ({
                    ...e,
                    status: e.status === 'cancelled_full_refund' ? 'cancelled' : e.status
                }))
            };
        }
    );

    // calculate overlap
    useEffect(() => {
        if (!bookingsData?.bookingItems) {
            return;
        }

        setOverlap(
            bookingsData.bookingItems.filter(
                bookingItem =>
                    !['cancelled', 'cancelled_full_refund', 'denied'].includes(
                        bookingItem.status
                    ) &&
                    bookingsData.bookingItems
                        .filter(e => e.id !== bookingItem.id)
                        .findIndex(
                            e =>
                                // isBetween parameters: 'undefined' indicates granularity and '[]' indicates inclusion of a value
                                moment(e.activity_start).isBetween(
                                    bookingItem.activity_start,
                                    bookingItem.activity_end,
                                    undefined,
                                    '[]'
                                ) ||
                                moment(e.activity_end).isBetween(
                                    bookingItem.activity_start,
                                    bookingItem.activity_end,
                                    undefined,
                                    '[]'
                                )
                        ) !== -1
            )
        );
    }, [bookingsData, setOverlap]);

    const bookingsForPrint =
        bookingsData?.bookingItems
            .filter(e => e.status === 'confirmed')
            // shallow copy + extend
            .map(e => ({
                full_start_datetime: moment(e.activity_start).format('dddd MMM DD h:mma'),
                ...e
            }))
            // sort by start
            .sort(
                (a, b) =>
                    new Date(a?.activity?.activity_start).getTime() -
                    new Date(b?.activity?.activity_start).getTime()
            ) || [];

    return (
        <>
            <Grid item container xs={12} spacing={2}>
                <Grid container className="hide-in-print">
                    {overlap.length !== 0 && !bookingsLoading && (
                        <Grid item xs={12} className="mb-2 mt-2">
                            <Alert severity="error">
                                You have activities (confirmed or unconfirmed) that overlap. Please
                                review your reservations to make sure this is correct.
                            </Alert>
                        </Grid>
                    )}
                </Grid>
                <Grid item xs={12} sm={6} className="hide-in-print">
                    <h2>
                        {label}
                        <span>{bookingsData?.bookingItemsCount} Activities</span>
                        {cart?.length > 0 && <span>All cart items: {cart.length}</span>}
                    </h2>
                </Grid>
                <Calendar
                    onEventClick={event => showModal(MODAL_TYPE.CALENDAR_EVENT_INFO, { event })}
                    bookings={bookingsData?.bookingItems}
                    bookingsLoading={bookingsLoading}
                    initialDate={moment()}
                    showCancelledDenied={false}
                    customButtons={{
                        prev: {
                            click() {
                                setStartDate(prevStartDate =>
                                    prevStartDate.clone().subtract(7, 'days')
                                );
                                setEndDate(prevEndDate => prevEndDate.clone().subtract(7, 'days'));
                            }
                        },
                        next: {
                            click() {
                                setStartDate(prevStartDate => prevStartDate.clone().add(7, 'days'));
                                setEndDate(prevEndDate => prevEndDate.clone().add(7, 'days'));
                            }
                        }
                    }}
                />
            </Grid>
            {/* Force the header to a new page*/}
            <Grid item xs={12} className="onlyPrint" style={{ pageBreakBefore: 'always' }}>
                <Typography variant="h6" className="onlyPrint">
                    {guest?.firstname} {guest?.lastname}
                    <br />
                    <small>{guest?.email}</small>
                </Typography>
            </Grid>
            <Grid item xs={12} className="onlyPrint">
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="left" style={{ width: 1, whiteSpace: 'nowrap' }}>
                                    Start Date &amp; Time
                                </TableCell>
                                <TableCell>Name</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {bookingsForPrint.map(bookingItem => (
                                <TableRow>
                                    <TableCell style={{ width: 1, whiteSpace: 'nowrap' }}>
                                        {bookingItem.full_start_datetime}
                                    </TableCell>
                                    <TableCell>{bookingItem?.activity?.name}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell
                                    style={{ borderBottom: 'none' }}
                                    align="right"
                                    colspan={2}
                                >
                                    Total activities: {bookingsForPrint.length}
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            </Grid>
        </>
    );
};

export default withTheme(CartPageCalendar);
